import type {ReviewSummary} from '../../../../../page-data/payloads/review-summaries'

function isNewer(a: ReviewSummary, b: ReviewSummary | undefined) {
  return !b || new Date(a.submittedAt) > new Date(b.submittedAt)
}

/**
 * Collapses a reviewer's review summaries down to a single standing review.
 *
 * A reviewer's standing review decision (APPROVED / CHANGES_REQUESTED) is not superseded by a later
 * comment-only review, so we can't simply keep the most recent summary per author. Instead we track
 * the latest opinionated review and the latest comment review separately, and let a later dismissal
 * clear the opinionated decision:
 *
 * - The latest opinionated review stands unless a later dismissal cleared it.
 * - Otherwise we fall back to the reviewer's latest comment-only review, if any.
 */
export function dedupeReviewSummaries(reviewSummaries: readonly ReviewSummary[]): ReviewSummary[] {
  const latestOpinionated = new Map<string, ReviewSummary>()
  const latestDismissal = new Map<string, ReviewSummary>()
  const latestComment = new Map<string, ReviewSummary>()

  for (const reviewSummary of reviewSummaries) {
    const author = reviewSummary.authorLogin
    switch (reviewSummary.state) {
      case 'APPROVED':
      case 'CHANGES_REQUESTED':
        if (isNewer(reviewSummary, latestOpinionated.get(author))) {
          latestOpinionated.set(author, reviewSummary)
        }
        break
      case 'DISMISSED':
        if (isNewer(reviewSummary, latestDismissal.get(author))) {
          latestDismissal.set(author, reviewSummary)
        }
        break
      case 'COMMENTED':
        if (isNewer(reviewSummary, latestComment.get(author))) {
          latestComment.set(author, reviewSummary)
        }
        break
    }
  }

  const authors = new Set([...latestOpinionated.keys(), ...latestComment.keys()])
  const dedupedReviewSummaries: ReviewSummary[] = []

  for (const author of authors) {
    const opinionated = latestOpinionated.get(author)
    const dismissal = latestDismissal.get(author)
    // The opinionated decision stands unless a later dismissal cleared it.
    if (opinionated && (!dismissal || new Date(opinionated.submittedAt) > new Date(dismissal.submittedAt))) {
      dedupedReviewSummaries.push(opinionated)
      continue
    }

    // No standing decision: fall back to the reviewer's latest comment review, if any.
    const comment = latestComment.get(author)
    if (comment) {
      dedupedReviewSummaries.push(comment)
    }
  }

  return dedupedReviewSummaries
}
